import React, {useState, useEffect, useRef} from 'react'
import {Button, Picture, Select, ButtonProps} from '_uiComponents'
import {FormattedMessage, useIntl} from 'react-intl'
import {Flex, Text, useMultiStyleConfig, TextProps, FlexProps, Box} from '@chakra-ui/react'
import {useCustomApi, useAnalytics, useViewport} from '_customHooks'
import {DataAction} from '_types'
import {setCountryCookie} from '_utils'

interface LocaleModalProps {
    title: string
    description: string
}

interface LocaleModalState {
    dataAction: DataAction
    countryName: string
    language: string
    languageList: Array<{label: string; value: string}>
    hostLink: string
}

/**
 * @name LocaleModal
 *
 * @component
 *
 * @description
 * LocaleModal renders the body of locale modale which contains a picture, title, description, some selects and a button to confirm change locale.
 *
 * @param {LocaleModalProps} props - Properties object
 * @param {string} props.title - Title for the locale modal.
 * @param {string} props.description - Description for the locale modal.
 * @returns {JSX.Element} Rendered locale modal.
 */
const LocaleModal = ({title, description}: LocaleModalProps): JSX.Element => {
    const styles = useMultiStyleConfig('LocaleModal') as Record<string, TextProps & FlexProps>
    const {formatMessage} = useIntl()
    const {useRetrieveLocales, useSetLocale} = useCustomApi()
    const {analyticsEventChangeLocale} = useAnalytics()
    const {mutate} = useSetLocale()
    const {isMobile} = useViewport()

    const modalRef = useRef<HTMLDivElement>(null)
    const {data: countriesData, isLoading: isCountriesLoading} = useRetrieveLocales()

    const [localeModalState, setLocaleModalState] = useState<LocaleModalState>({
        dataAction: {action: 'Search-Show', queryParams: ''},
        countryName: '',
        language: '',
        languageList: [],
        hostLink: '',
    })

    const [countryList, setCountryList] = useState<Array<{label: string; value: string}>>([{label: '', value: ''}])

    const altImgLabel = formatMessage({id: 'locale.alt.image', defaultMessage: 'Immagine del pop-up cambio locale'})
    const altCountrySelectLabel = formatMessage({id: 'label.form.country', defaultMessage: 'Paese di consegna'})
    const altLanguageSelectLabel = formatMessage({id: 'label.form.lang', defaultMessage: 'Lingua'})

    const updateCountry = (countryName: string | null) => {
        if (!countryName) return

        const selectedCountry = countriesData?.locale.allCountries.find(({country}) => country === countryName) ?? null

        if (selectedCountry) {
            setLocaleModalState((prev) => ({
                ...prev,
                countryName: selectedCountry?.country,
                language: selectedCountry?.localeLanguage,
                languageList: selectedCountry?.languages,
                hostLink: selectedCountry?.hostLink || '',
            }))
        }
    }

    const updateLanguage = (value: string | null) => {
        if (value) {
            setLocaleModalState((prev) => ({
                ...prev,
                language: value,
            }))
        }
    }

    const triggerChangeLocale = () => {
        const changeToLocaleId = localeModalState.language + '-' + localeModalState.countryName
        const changeToLocaleAliasUpper = localeModalState.language.replace('ch', 'zh') + '-' + localeModalState.countryName
        const changeToLocaleAlias = changeToLocaleAliasUpper.toLowerCase()
        mutate(
            {locale: changeToLocaleId, dataAction: localeModalState.dataAction},
            {
                onSuccess: (data) => {
                    analyticsEventChangeLocale({
                        action: 'confirm',
                        label: changeToLocaleAliasUpper,
                    })

                    setCountryCookie(localeModalState.countryName)
                    const switchDomain = !window.location.origin.includes(localeModalState.hostLink) && localeModalState.hostLink !== ''

                    if (data.success) {
                        const relativePath = data.redirectUrl

                        if (switchDomain) {
                            window.location.href = `${window.location.protocol}//${localeModalState.hostLink}/${changeToLocaleAlias}${relativePath}${window.location.search ?? ''}`
                        } else {
                            window.location.href = `/${changeToLocaleAlias}${relativePath}${window.location.search ?? ''}`
                        }
                    } else {
                        const currentPath = window.location.pathname
                        const newPath = currentPath.replace(/\/[a-z]{2}-[a-z]{2}/i, `/${changeToLocaleAlias}`)

                        if (switchDomain) {
                            window.location.href = `${window.location.protocol}//${localeModalState.hostLink}${newPath}${window.location.search ?? ''}`
                        } else {
                            window.location.href = newPath + (window.location.search ?? '')
                        }
                    }
                },
            },
        )
    }

    useEffect(() => {
        if (countriesData) {
            const initialCountry = countriesData?.locale

            const countries =
                countriesData?.locale.allCountries.map((country) => ({
                    label: country.traductionCountry,
                    value: country.country,
                })) ?? []

            setLocaleModalState((prev) => ({
                ...prev,
                countryName: initialCountry?.countryCode,
                language: initialCountry?.localeLanguage,
                languageList: initialCountry?.languages,
            }))

            setCountryList(countries)
        }
    }, [countriesData])

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const action = document.querySelector('div[data-action]')?.getAttribute('data-action') || undefined
            const cgid = document.querySelector('div[data-cgid]')?.getAttribute('data-cgid') || undefined

            setLocaleModalState((prev) => ({
                ...prev,
                dataAction: {
                    action: action ?? undefined,
                    queryParams: cgid ?? undefined,
                },
            }))
        }
    }, [])

    return (
        <>
            <Flex {...styles.picture_wrap}>
                <Picture
                    imagesm={
                        'https://shop.brunellocucinelli.com/on/demandware.static/-/Library-Sites-SiteGenesisSharedLibrary/default/dwc0107070/selectLanguage/CAMBIOLINGUA-MOBILE.jpg'
                    }
                    imagemd={
                        'https://shop.brunellocucinelli.com/on/demandware.static/-/Library-Sites-SiteGenesisSharedLibrary/default/dw5c6178b7/selectLanguage/CAMBIOLINGUA-TABLET.jpg'
                    }
                    imagelg={
                        'https://shop.brunellocucinelli.com/on/demandware.static/-/Library-Sites-SiteGenesisSharedLibrary/default/dwc0107070/selectLanguage/CAMBIOLINGUA-DESKTOP.jpg'
                    }
                    alt={altImgLabel}
                    objectFit={isMobile ? 'cover' : 'contain'}
                    height={isMobile ? 'unset' : '100%'}
                    w={'100%'}
                />
            </Flex>

            <Flex {...styles.wrapper} ref={modalRef} data-testid="locale-modal">
                <Text {...styles.title}>{title}</Text>

                <Text {...styles.description}>{description}</Text>

                <Box sx={styles.wrapperSelect}>
                    <Select
                        name="Country"
                        label={altCountrySelectLabel}
                        options={countryList}
                        value={localeModalState.countryName}
                        onChange={updateCountry}
                        isDisabled={isCountriesLoading}
                        dataTestId="select-country"
                        menuPortalTarget={modalRef.current}
                        menuPlacement={isMobile ? 'bottom' : 'top'}
                    />

                    <Select
                        name="Languages"
                        label={altLanguageSelectLabel}
                        options={localeModalState.languageList}
                        value={localeModalState.language}
                        onChange={updateLanguage}
                        isDisabled={isCountriesLoading}
                        dataTestId="select-language"
                        menuPortalTarget={modalRef.current}
                        menuPlacement={isMobile ? 'top' : localeModalState.languageList.length > 2 ? 'top' : 'auto'}
                    />
                </Box>

                <Button
                    {...(styles.button as ButtonProps)}
                    variant="primary"
                    type="button"
                    size="changelocale"
                    onClick={triggerChangeLocale}
                    data-testid="changeLocale-submit"
                    isDisabled={isCountriesLoading}
                >
                    <FormattedMessage id="label.form.button" defaultMessage="Conferma" />
                </Button>
            </Flex>
        </>
    )
}

export {LocaleModal}
